import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import {
  getUpcomingNights,
  hasTheme,
  countdownLabel,
} from '../../data/events';
import Modal from '../Modal';
import EventDetail from '../EventDetail';
import useInView from '../../hooks/useInView';
import useEvents from '../../hooks/useEvents';
import styles from './NightsStrip.module.css';

/**
 * Home-page "Revive Nights Pittsburgh" block.
 *
 * Mirrors Social Dallas's Texas Trust Theater block:
 *   centered single column
 *   big serif title
 *   small uppercase subhead
 *   single button
 *   small photo strip beneath
 *
 * Translucent giant "Worship" word sits behind the title block
 * (their pattern, where a faded "SUNDAY" sits behind).
 *
 * The month carousel + click-to-open detail modal are our additions
 * for the auto-time-tracked monthly experiences.
 */
export default function NightsStrip() {
  const events = useEvents();
  const upcoming = useMemo(() => getUpcomingNights(new Date(), events), [events]);
  const nextId = upcoming[0]?.id;
  const [openId, setOpenId] = useState(null);
  const [headRef, headIn] = useInView({ threshold: 0.18 });
  const trackRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const ordered = useMemo(() => {
    const upcomingSet = new Set(upcoming.map((e) => e.id));
    const past = events.filter((e) => !upcomingSet.has(e.id)).sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );
    return [...upcoming, ...past];
  }, [upcoming, events]);

  const updateScrollState = () => {
    const el = trackRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    if (!trackRef.current || !nextId) return;
    const el = trackRef.current.querySelector(`[data-id="${nextId}"]`);
    if (el) {
      trackRef.current.scrollTo({ left: el.offsetLeft - 24, behavior: 'instant' });
    }
    updateScrollState();
  }, [nextId]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, []);

  const nudge = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const firstTile = el.querySelector('button');
    const step = firstTile ? firstTile.offsetWidth + 10 : 240;
    el.scrollBy({ left: dir * step * 2, behavior: 'smooth' });
  };

  const openEvent = ordered.find((e) => e.id === openId);

  return (
    <section id="nights-strip" className={styles.section}>
      <span className={styles.bgWord} aria-hidden="true">Worship</span>

      <div className={styles.shell}>
        <header className={`${styles.head} ${headIn ? styles.headIn : ''}`} ref={headRef}>
          <h2 className={styles.title}>
            <span className={styles.titleLine}>Come to our</span>
            <span className={styles.titleLine}>
              <em>Next gathering!</em>
            </span>
          </h2>

          <p className={styles.subhead}>Monthly Experiences</p>

          <a className={styles.learnMore} href="/nights">
            <span>Learn More</span>
            <span className={styles.learnArrow}>→</span>
          </a>
        </header>

        <div className={styles.trackWrap}>
          <div className={styles.track} ref={trackRef} role="list">
            {ordered.map((ev) => {
              const isNext = ev.id === nextId;
              const isPast = !upcoming.some((u) => u.id === ev.id);
              const dayNum = !ev.tbd
                ? new Date(ev.date + 'T00:00:00').getDate()
                : null;
              const venue = ev.isSabbath
                ? 'No public gathering'
                : ev.locationShort ||
                  (ev.tbd ? 'Venue TBD' : ev.locationName);
              const theme = ev.isSabbath ? ev.theme : hasTheme(ev) ? ev.theme : '';
              return (
                <button
                  key={ev.id}
                  type="button"
                  role="listitem"
                  data-id={ev.id}
                  className={`${styles.tile} ${isNext ? styles.tileNext : ''} ${isPast ? styles.tilePast : ''}`}
                  onClick={() => setOpenId(ev.id)}
                >
                  {isNext && (
                    <span className={styles.nextTag}>
                      {countdownLabel(ev) ? `Up Next · ${countdownLabel(ev)}` : 'Up Next'}
                    </span>
                  )}
                  <span className={styles.tileMonth}>{ev.monthLabel}</span>
                  <span className={styles.tileInfo}>
                    <span className={styles.tileDate}>
                      {ev.isSabbath
                        ? 'All month'
                        : ev.tbd
                          ? 'Date TBD'
                          : `${ev.monthLabel.slice(0, 3)} ${dayNum} · ${ev.timeLabel}`}
                    </span>
                    <span className={styles.tileVenue}>{venue}</span>
                    {theme && <span className={styles.tileTheme}>{theme}</span>}
                  </span>
                </button>
              );
            })}
          </div>
          <div className={styles.trackFade} aria-hidden="true" />
        </div>

        <div className={styles.trackNav}>
          <button
            type="button"
            className={styles.trackNavBtn}
            onClick={() => nudge(-1)}
            disabled={!canScrollLeft}
            aria-label="Previous months"
          >
            <span aria-hidden="true">←</span>
          </button>
          <button
            type="button"
            className={styles.trackNavBtn}
            onClick={() => nudge(1)}
            disabled={!canScrollRight}
            aria-label="Next months"
          >
            <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {openEvent && (
          <Modal open onClose={() => setOpenId(null)} label={`Revive Night: ${openEvent.edition}`} size="md">
            <EventDetail event={openEvent} />
          </Modal>
        )}
      </AnimatePresence>
    </section>
  );
}
